import {
  Modal,
  Stack,
  Text,
  Group,
  Button,
  Badge,
  ThemeIcon,
  Loader,
  Center,
  Accordion,
  Alert,
  Divider,
} from '@mantine/core';
import {
  CheckCircle,
  AlertTriangle,
  AlertCircle,
  Inbox,
  Download,
  Info,
} from 'react-feather';
import type { BackfillHistoricalSummary } from '../../../../services/api/intake';

interface PullHistoricalResponsesModalProps {
  opened: boolean;
  onClose: () => void;
  /** Active while the backfill request is in-flight */
  loading: boolean;
  /** Result summary once the backfill completes */
  summary: BackfillHistoricalSummary | null;
  /** Error message when the backfill request fails */
  error: string | null;
}

/**
 * MED-942: Result modal for the "Pull Historical Responses" action on the
 * Matching Form sub-tab. Renders the per-outcome summary the CM needs to see
 * (matched / already-processed / unmatched / ambiguous / no-email / failed),
 * an explicit empty state when zero submissions came back, and clear loading
 * + error surfaces.
 */
export function PullHistoricalResponsesModal({
  opened,
  onClose,
  loading,
  summary,
  error,
}: PullHistoricalResponsesModalProps) {
  return (
    <Modal
      opened={opened}
      onClose={loading ? () => undefined : onClose}
      withCloseButton={!loading}
      closeOnClickOutside={!loading}
      closeOnEscape={!loading}
      title={
        <Group gap="sm">
          <ThemeIcon color="medleyGreen" variant="light" size="lg" radius="xl">
            <Download size={18} />
          </ThemeIcon>
          <Text fw={600}>Pull Historical Responses</Text>
        </Group>
      }
      size="lg"
      centered
    >
      {loading && <LoadingState />}
      {!loading && error && <ErrorState error={error} onClose={onClose} />}
      {!loading && !error && summary && (
        <SummaryView summary={summary} onClose={onClose} />
      )}
    </Modal>
  );
}

function LoadingState() {
  return (
    <Center py="xl">
      <Stack align="center" gap="md">
        <Loader size="md" color="medleyGreen" />
        <Text size="sm" c="dimmed">
          Pulling submissions from Paperform&hellip;
        </Text>
        <Text size="xs" c="dimmed">
          This usually takes a few seconds.
        </Text>
      </Stack>
    </Center>
  );
}

function ErrorState({ error, onClose }: { error: string; onClose: () => void }) {
  return (
    <Stack gap="md">
      <Alert icon={<AlertCircle size={16} />} color="dangerRed" variant="light">
        <Text size="sm">{error}</Text>
      </Alert>
      <Text size="xs" c="dimmed">
        No SGOS data was changed.
      </Text>
      <Group justify="flex-end">
        <Button variant="default" onClick={onClose}>
          Close
        </Button>
      </Group>
    </Stack>
  );
}

function SummaryView({
  summary,
  onClose,
}: {
  summary: BackfillHistoricalSummary;
  onClose: () => void;
}) {
  const { total_submissions_fetched } = summary;
  const matchedCount = summary.matched.length;
  const unmatchedCount = summary.unmatched.length;
  const ambiguousCount = summary.ambiguous.length;
  const noEmailCount = summary.no_email.length;
  const failedCount = summary.failed.length;
  const newlyProcessed =
    matchedCount + unmatchedCount + ambiguousCount + noEmailCount + failedCount;

  // AC #16: zero new submissions → explicit empty state, never an ambiguous
  // "succeeded with no detail" panel. Two cases land here:
  //   1. Paperform returned zero submissions at all
  //   2. Everything Paperform returned was already in form_submissions
  if (newlyProcessed === 0) {
    return (
      <Stack gap="md">
        <Center py="md">
          <Stack align="center" gap="sm">
            <ThemeIcon color="gray" variant="light" size={48} radius="xl">
              <Inbox size={24} />
            </ThemeIcon>
            <Text fw={600}>Nothing new to import</Text>
            <Text size="sm" c="dimmed" ta="center">
              {total_submissions_fetched === 0
                ? 'Paperform returned no submissions for this form.'
                : `All ${total_submissions_fetched} submissions on this form were already imported into SGOS.`}
            </Text>
          </Stack>
        </Center>
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Close
          </Button>
        </Group>
      </Stack>
    );
  }

  return (
    <Stack gap="md">
      <Text size="sm" c="dimmed">
        Fetched {total_submissions_fetched} submissions from Paperform.
      </Text>

      <Stack gap="xs">
        <CountRow
          icon={<CheckCircle size={16} />}
          color="medleyGreen"
          label="Matched and imported"
          count={matchedCount}
        />
        <CountRow
          icon={<Info size={16} />}
          color="gray"
          label="Skipped, already processed"
          count={summary.already_processed}
        />
        <CountRow
          icon={<AlertTriangle size={16} />}
          color="warningAmber"
          label="Unmatched (no participant with that email)"
          count={unmatchedCount}
          emphasize={unmatchedCount > 0}
        />
        <CountRow
          icon={<AlertTriangle size={16} />}
          color="warningAmber"
          label="Skipped, ambiguous (multiple matches)"
          count={ambiguousCount}
        />
        <CountRow
          icon={<AlertTriangle size={16} />}
          color="warningAmber"
          label="Skipped, no email on submission"
          count={noEmailCount}
        />
        <CountRow
          icon={<AlertCircle size={16} />}
          color="dangerRed"
          label="Failed"
          count={failedCount}
        />
      </Stack>

      {(matchedCount > 0 ||
        unmatchedCount > 0 ||
        ambiguousCount > 0 ||
        noEmailCount > 0 ||
        failedCount > 0) && (
        <>
          <Divider />
          <Accordion variant="separated" radius="sm" multiple>
            {matchedCount > 0 && (
              <Accordion.Item value="matched">
                <Accordion.Control>
                  <Group gap="xs">
                    <Text size="sm" fw={600}>
                      Matched participants
                    </Text>
                    <Badge color="medleyGreen" variant="light">
                      {matchedCount}
                    </Badge>
                  </Group>
                </Accordion.Control>
                <Accordion.Panel>
                  <Stack gap={4}>
                    {summary.matched.map((m) => (
                      <Text key={m.paperform_submission_id} size="xs" c="dimmed">
                        {m.participant_email}{' '}
                        <Text component="span" c="dimmed" inherit>
                          (submission {m.paperform_submission_id})
                        </Text>
                      </Text>
                    ))}
                  </Stack>
                </Accordion.Panel>
              </Accordion.Item>
            )}

            {unmatchedCount > 0 && (
              <Accordion.Item value="unmatched">
                <Accordion.Control>
                  <Group gap="xs">
                    <Text size="sm" fw={600}>
                      Unmatched emails &mdash; investigate
                    </Text>
                    <Badge color="warningAmber" variant="light">
                      {unmatchedCount}
                    </Badge>
                  </Group>
                </Accordion.Control>
                <Accordion.Panel>
                  <Stack gap={4}>
                    {summary.unmatched.map((u) => (
                      <Text key={u.paperform_submission_id} size="xs">
                        <Text component="span" fw={500} inherit>
                          {u.participant_email}
                        </Text>{' '}
                        <Text component="span" c="dimmed" inherit>
                          (submission {u.paperform_submission_id})
                        </Text>
                      </Text>
                    ))}
                  </Stack>
                </Accordion.Panel>
              </Accordion.Item>
            )}

            {ambiguousCount > 0 && (
              <Accordion.Item value="ambiguous">
                <Accordion.Control>
                  <Group gap="xs">
                    <Text size="sm" fw={600}>
                      Ambiguous matches
                    </Text>
                    <Badge color="warningAmber" variant="light">
                      {ambiguousCount}
                    </Badge>
                  </Group>
                </Accordion.Control>
                <Accordion.Panel>
                  <Stack gap={4}>
                    {summary.ambiguous.map((a) => (
                      <Text key={a.paperform_submission_id} size="xs">
                        <Text component="span" fw={500} inherit>
                          {a.participant_email}
                        </Text>{' '}
                        <Text component="span" c="dimmed" inherit>
                          matched {a.match_count} participants &middot; submission{' '}
                          {a.paperform_submission_id}
                        </Text>
                      </Text>
                    ))}
                  </Stack>
                </Accordion.Panel>
              </Accordion.Item>
            )}

            {noEmailCount > 0 && (
              <Accordion.Item value="no-email">
                <Accordion.Control>
                  <Group gap="xs">
                    <Text size="sm" fw={600}>
                      Submissions with no participant_email
                    </Text>
                    <Badge color="warningAmber" variant="light">
                      {noEmailCount}
                    </Badge>
                  </Group>
                </Accordion.Control>
                <Accordion.Panel>
                  <Stack gap={4}>
                    {summary.no_email.map((n) => (
                      <Text key={n.paperform_submission_id} size="xs" c="dimmed">
                        submission {n.paperform_submission_id}
                      </Text>
                    ))}
                  </Stack>
                </Accordion.Panel>
              </Accordion.Item>
            )}

            {failedCount > 0 && (
              <Accordion.Item value="failed">
                <Accordion.Control>
                  <Group gap="xs">
                    <Text size="sm" fw={600}>
                      Failed
                    </Text>
                    <Badge color="dangerRed" variant="light">
                      {failedCount}
                    </Badge>
                  </Group>
                </Accordion.Control>
                <Accordion.Panel>
                  <Stack gap={4}>
                    {summary.failed.map((f) => (
                      <Text key={f.paperform_submission_id} size="xs">
                        <Text component="span" c="dangerRed" inherit>
                          {f.error}
                        </Text>{' '}
                        <Text component="span" c="dimmed" inherit>
                          (submission {f.paperform_submission_id})
                        </Text>
                      </Text>
                    ))}
                  </Stack>
                </Accordion.Panel>
              </Accordion.Item>
            )}
          </Accordion>
        </>
      )}

      <Group justify="flex-end">
        <Button variant="default" onClick={onClose}>
          Close
        </Button>
      </Group>
    </Stack>
  );
}

function CountRow({
  icon,
  color,
  label,
  count,
  emphasize = false,
}: {
  icon: React.ReactNode;
  color: string;
  label: string;
  count: number;
  emphasize?: boolean;
}) {
  return (
    <Group justify="space-between" wrap="nowrap">
      <Group gap="xs" wrap="nowrap">
        <ThemeIcon color={color} variant="light" size="sm" radius="xl">
          {icon}
        </ThemeIcon>
        <Text size="sm" fw={emphasize ? 600 : 400}>
          {label}
        </Text>
      </Group>
      <Badge color={color} variant={emphasize ? 'filled' : 'light'}>
        {count}
      </Badge>
    </Group>
  );
}

